// components/Chatbot/ChatbotWindow.tsx
import React, { useRef, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Chip, CircularProgress, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { Message, ChatOption } from './types';

interface ChatbotWindowProps {
  messages: Message[];
  isLoading: boolean;
  options: ChatOption[];
  onSendMessage: (message: string) => void;
  onClose: () => void;
  className?: string;
}

const ChatbotWindow: React.FC<ChatbotWindowProps> = ({ messages, isLoading, options, onSendMessage }) => {
  const [inputValue, setInputValue] = React.useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOptionClick = (option: ChatOption) => {
    onSendMessage(option.value);
  };

  const getRGBColor = (color: string) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  };

  return (
    <Paper
      elevation={24}
      sx={{
        position: 'absolute',
        top: 80,
        right: 0,
        width: 400,
        height: 600,
        maxHeight: '80vh',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: `rgba(${getRGBColor(theme.palette.background.paper)}, 0.8)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid rgba(${getRGBColor(theme.palette.primary.main)}, 0.2)`,
        boxShadow: `0 8px 32px rgba(${getRGBColor(theme.palette.primary.dark)}, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.3)`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <SmartToyIcon sx={{ color: theme.palette.primary.contrastText, fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
            Logistics Assistant
          </Typography>
          <Typography variant="caption" sx={{ color: `rgba(${getRGBColor(theme.palette.primary.contrastText)}, 0.8)` }}>
            Online - Ready to help
          </Typography>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: `rgba(${getRGBColor(theme.palette.background.default)}, 0.3)`,
        }}
      >
        {messages.map(message => (
          <Box key={message.id}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              {message.sender === 'bot' && <SmartToyIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />}
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  backgroundColor:
                    message.sender === 'user'
                      ? theme.palette.primary.main
                      : `rgba(${getRGBColor(theme.palette.background.paper)}, 0.7)`,
                  backdropFilter: 'blur(10px)',
                  border:
                    message.sender === 'user'
                      ? `1px solid rgba(${getRGBColor(theme.palette.primary.light)}, 0.5)`
                      : `1px solid rgba(${getRGBColor(theme.palette.primary.main)}, 0.1)`,
                  borderRadius: 2,
                  borderBottomRightRadius: message.sender === 'user' ? 0 : 2,
                  borderBottomLeftRadius: message.sender === 'bot' ? 0 : 2,
                  color: message.sender === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                }}
              >
                <Typography variant="body2">{message.text}</Typography>
              </Paper>
              {message.sender === 'user' && <PersonIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />}
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                ml: message.sender === 'bot' ? 5 : 0,
                mr: message.sender === 'user' ? 5 : 0,
                textAlign: message.sender === 'user' ? 'right' : 'left',
                color: theme.palette.text.secondary,
              }}
            >
              {message.timestamp.toLocaleTimeString()}
            </Typography>
          </Box>
        ))}

        {/* Options */}
        {options.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
            {options.map(option => (
              <Chip
                key={option.id}
                label={option.label}
                onClick={() => handleOptionClick(option)}
                sx={{
                  backgroundColor: `rgba(${getRGBColor(theme.palette.primary.main)}, 0.1)`,
                  border: `1px solid rgba(${getRGBColor(theme.palette.primary.main)}, 0.3)`,
                  backdropFilter: 'blur(5px)',
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: `rgba(${getRGBColor(theme.palette.primary.main)}, 0.2)`,
                    transform: 'scale(1.05)',
                    borderColor: theme.palette.primary.main,
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          backgroundColor: `rgba(${getRGBColor(theme.palette.background.paper)}, 0.7)`,
          backdropFilter: 'blur(10px)',
          borderTop: `1px solid rgba(${getRGBColor(theme.palette.primary.main)}, 0.2)`,
          display: 'flex',
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: `rgba(${getRGBColor(theme.palette.background.default)}, 0.5)`,
              backdropFilter: 'blur(5px)',
              '& fieldset': {
                borderColor: `rgba(${getRGBColor(theme.palette.primary.main)}, 0.3)`,
              },
              '&:hover fieldset': {
                borderColor: `rgba(${getRGBColor(theme.palette.primary.main)}, 0.5)`,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            backdropFilter: 'blur(10px)',
            minWidth: 'auto',
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            },
            '&:disabled': {
              background: theme.palette.action.disabledBackground,
            },
          }}
        >
          <SendIcon />
        </Button>
      </Box>
    </Paper>
  );
};

export default ChatbotWindow;
